<template>
  <div class="zip-code-with-autocomplete">
    <div>
      <TextInput
        ref="textInputRef"
        type="text"
        v-model="inputPostCode"
        placeholder="Deine Postleitzahl"
        :maxlength="5"
        data-private-info="true"
        @click="suggestionListFocus"
        @focus="suggestionListFocus"
        :inputTestId="props.zipInputDataTestId"
        :validateInput="validateZip"
        custom-error-color="var(--SysDarkError)"
        :is-number="true"
      />
      <custom-progress v-show="fetchingSuggestion" size="32" />
      <div
        class="value_clone"
        v-show="inputCity.length > 0"
        data-private-info="true"
      >
        <span class="clone_postcode">{{ inputPostCode }}</span>
        <span class="clone_city">{{ inputCity }}</span>
      </div>
    </div>

    <ul
      v-if="revealSuggestionList"
      role="listbox"
      aria-label="Deine Postleitzahl und Stadt"
      :aria-activedescendant="`suggestion-item-${suggestionListCurrentFocus}`"
      class="suggestion-list var-elevation--9"
      v-show="revealSuggestionList"
      tabindex="0"
      data-private-info="true"
      :data-testid="props.suggestionListTestId"
      @keydown.down="suggestionListArrowDown"
      @keydown.up="suggestionListArrowUp"
      @keydown.enter="
        (e) => selectSuggestion(e, suggestionList[suggestionListCurrentFocus])
      "
      @blur="suggestionListBlur"
    >
      <li
        v-for="(suggestionItem, index) in suggestionList"
        :id="`suggestion-item-${index}`"
        role="option"
        class="suggestion-item"
        :focus="index === suggestionListCurrentFocus"
        @click="(e: MouseEvent) => selectSuggestion(e, suggestionItem)"
        :class="{ focused: index === suggestionListCurrentFocus }"
      >
        {{ inputPostCode }} {{ suggestionItem }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import './ZipCodeWithAutocomplete.scss';
import '@material/web/progress/circular-progress';
import TextInput from 'ewi-ui/components/1-atoms/VForm/TextInput';
import { suggestionListStore } from './_store/suggestionList';
import CustomProgress from 'ewi-ui/components/1-atoms/CustomProgress/CustomProgress.vue';

export interface ZipCodeWithAutocompleteProps {
  postcode: string;
  city: string;
  error: boolean | string;
  zipInputDataTestId?: string;
  suggestionListTestId?: string;
}

const props = withDefaults(defineProps<ZipCodeWithAutocompleteProps>(), {
  zipInputDataTestId: 'zip-single-option',
  suggestionListTestId: '',
});

const emit = defineEmits(['update:postcode', 'update:city']);

const store = suggestionListStore();
const fetchingSuggestion = ref(false);
const suggestionList = ref<string[]>([]);
const suggestionListCurrentFocus = ref(-1);
const openSuggestionList = ref(false);
const inputPostCode = ref(props.postcode);
const inputCity = ref(props.city);
const textInputRef = ref();

const revealSuggestionList = computed(
  () =>
    suggestionList.value !== null &&
    suggestionList.value.length > 1 &&
    (inputCity.value === '' || openSuggestionList.value),
);

const suggestionLookup = async () => {
  fetchingSuggestion.value = true;
  suggestionList.value = [];
  suggestionList.value = await store.getSuggestionFromZipCode(
    inputPostCode.value,
  );

  textInputRef.value?.validate();
  fetchingSuggestion.value = false;
  suggestionListCurrentFocus.value = 0;
};

const suggestionListArrowDown = () => {
  suggestionListCurrentFocus.value++;
  if (suggestionListCurrentFocus.value >= suggestionList.value.length) {
    suggestionListCurrentFocus.value = suggestionList.value.length - 1;
  }
};

const suggestionListArrowUp = () => {
  suggestionListCurrentFocus.value--;
  if (suggestionListCurrentFocus.value < 0) {
    suggestionListCurrentFocus.value = 0;
  }
};

const suggestionListFocus = () => {
  openSuggestionList.value = true;
};

const suggestionListBlur = () => {
  openSuggestionList.value = false;
};

const selectSuggestion = (
  event: MouseEvent | KeyboardEvent | null,
  cityValue: string,
) => {
  if (event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  inputCity.value = cityValue;
  suggestionListCurrentFocus.value = suggestionList.value.indexOf(cityValue);
  openSuggestionList.value = false;
};

const handlePostCode = async (newZipValue: string) => {
  suggestionListCurrentFocus.value = -1;
  if (newZipValue.length === 5) {
    await suggestionLookup();
  } else {
    suggestionList.value = [];
    inputCity.value = '';
  }
};

watch(
  () => props.postcode,
  (newVal) => {
    inputPostCode.value = newVal;
  },
);

watch(
  () => props.city,
  (newVal) => {
    inputCity.value = newVal;
  },
);
watch(inputCity, (newVal) => {
  emit('update:city', newVal);
});

// Watchers
watch(inputPostCode, async (newVal) => {
  emit('update:postcode', newVal);
  return await handlePostCode(newVal);
});

watch(suggestionList, (newSuggestionList) => {
  if (newSuggestionList !== null && newSuggestionList.length === 1) {
    selectSuggestion(null, newSuggestionList[0]);
  }
  if (newSuggestionList !== null && newSuggestionList.length === 0) {
    inputCity.value = ' ';
  }
});

watch(suggestionListCurrentFocus, (newFocus) => {
  if (newFocus > -1) {
    nextTick(() => {
      if (newFocus === 0) {
        const focusedElement = document?.querySelector('.suggestion-list');
        if (focusedElement) {
          (focusedElement as HTMLElement).focus();
        }
      }
      const focusedElement = document.querySelector('.suggestion-item.focused');
      if (focusedElement) {
        focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
  }
});

const validateZip = computed(() => {
  const returned = () =>
    props.error ? props.error : store.error ? store.error : true;
  return returned;
});

watch(
  () => props.error,
  () => {
    textInputRef.value?.validate();
  },
);

watch(
  () => store.error,
  () => {
    textInputRef.value?.validate();
  },
);
</script>
